import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLBody = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Document Architecture</div>
                <h1>The &lt;body&gt; Canvas: The Interface of the Web</h1>
                <p className="hero-lead">
                    The <code>&lt;body&gt;</code> element is much more than a container; it is the
                    <strong>root of the renderable tree</strong> and the primary event hub
                    for user interaction. Master the mechanics of the document lifecycle,
                    global state management, and semantic orchestration.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Architectural Canvas</h2>
                <p>
                    In the browser's rendering engine, the <code>&lt;body&gt;</code> establishes
                    the <strong>primary viewport context</strong>. While the <code>&lt;html&gt;</code>
                    element represents the entire document (including metadata), the
                    <code>&lt;body&gt;</code> is the entry point for the <strong>Accessibility
                        Tree</strong> and the <strong>Box Model</strong>.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Layout Root</h3>
                        <p>The body acts as the root Block Formatting Context (BFC). It dictates
                            how floats interact and how margins collapse for the entire application.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Interaction Hub</h3>
                        <p>Most events (clicks, scrolls, keypresses) eventually bubble up to the
                            body, making it the ideal location for global event delegation.</p>
                    </div>
                </div>

                <ProTip><p><strong>Senior Insight:</strong> Modern CSS features like
                        <code>scroll-behavior: smooth</code> and
                        <code>scrollbar-gutter: stable</code> are often applied directly to
                        the <code>&lt;body&gt;</code> or <code>&lt;html&gt;</code> tags to
                        ensure consistent UI behavior across the entire application.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Understanding the &lt;body&gt; Element</h2>
                <p>
                    The <code>&lt;body&gt;</code> element contains <strong>all visible content</strong> of your web page:
                    text, images, videos, links, formseverything users see and interact with.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Body Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>My Page</title>
</head>
<body>
    <!-- All visible content goes here --&gt;
    <h1>Welcome to My Website</h1>
    <p>This is a paragraph of text.</p>
</body>
</html>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Key Fact:</strong> There should be exactly <em>one</em> <code>&lt;body&gt;</code> element
                        per HTML document. It comes after the <code>&lt;head&gt;</code> and before the closing <code>&lt;/html&gt;</code> tag.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>3. The Global Attribute Power Suite</h2>
                <p>
                    The <code>&lt;body&gt;</code> element supports the full suite of global
                    attributes, which are often used to manage application-wide state or
                    behavior.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>data-* Attributes</h3>
                        <p>Used to store custom private data. Perfect for storing theme names,
                            user permissions, or A/B testing IDs that scripts can read.</p>
                        <CodeBlock language="html" code={`<body data-theme="dark" data-auth="true">`} />
                    </div>
                    <div className="concept-card">
                        <h3>contenteditable</h3>
                        <p>When set to <code>true</code> on the body, the entire page becomes
                            a WYSIWYG editor. Often used by "no-code" design tools.</p>
                        <CodeBlock language="html" code={`<body contenteditable="true">`} />
                    </div>
                </div>

                <ProTip><p><strong>Senior Engineering Tip:</strong> Instead of using inline
                        <code>&lt;style&gt;</code> tags for themes, apply a <code>data-theme</code>
                        attribute to the body and use CSS Variables:
                        <code>body[data-theme='dark'] &#123; --bg: #000; &#125;</code>. This
                        is the modern, performant standard for theme switching.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. The Core Document Lifecycle (Events)</h2>
                <p>
                    The <code>&lt;body&gt;</code> element is the primary listener for
                    document-level lifecycle events. Understanding the order of these
                    events is critical for front-end performance.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>Target</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>DOMContentLoaded</code></td>
                                <td><code>document</code></td>
                                <td>HTML is loaded and DOM tree is built. Scripts can run.</td>
                            </tr>
                            <tr>
                                <td><code>load</code></td>
                                <td><code>window</code></td>
                                <td>Everything (images, CSS, subframes) is fully loaded.</td>
                            </tr>
                            <tr>
                                <td><code>beforeunload</code></td>
                                <td><code>window</code></td>
                                <td>Fires just before user leaves. Use to prompt "Save changes?".</td>
                            </tr>
                            <tr>
                                <td><code>visibilitychange</code></td>
                                <td><code>document</code></td>
                                <td>Fires when user switches tabs or minimizes the window.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Professional Lifecycle Monitoring</span>
                    </div>
                    <CodeBlock language="javascript" code={`// The Modern Pattern (Non-blocking)
document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM Ready. Starting light-weight init...');
});

window.addEventListener('load', () => {
    console.log('Heavy assets ready. Starting analytics/trackers...');
});

document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        console.log('User left tab. Pausing video/sync.');
    } else {
        console.log('User returned. Resuming...');
    }
});`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Semantic Body Structure</h2>
                <p>
                    Modern HTML5 provides semantic elements to structure your body content meaningfully.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Semantic Page Layout</span>
                    </div>
                    <CodeBlock language="html" code={`<body>
    <!-- Page header (logo, navigation) --&gt;
    <header>
        <nav>
            <ul>
                <li><a href="/">Home</a></li>
                <li><a href="/about">About</a></li>
                <li><a href="/contact">Contact</a></li>
            </ul>
        </nav>
    </header>

    <!-- Main content area --&gt;
    <main>
        <!-- Featured content --&gt;
        <section id="hero">
            <h1>Welcome to Our Website</h1>
            <p>Discover amazing content.</p>
        </section>

        <!-- Article or blog post --&gt;
        <article>
            <h2>Article Title</h2>
            <p>Article content goes here...</p>
        </article>

        <!-- Sidebar content --&gt;
        <aside>
            <h3>Related Links</h3>
            <ul>
                <li><a href="/link1">Link 1</a></li>
                <li><a href="/link2">Link 2</a></li>
            </ul>
        </aside>
    </main>

    <!-- Page footer (copyright, links) --&gt;
    <footer>
        <p>&copy; 2025 My Company. All rights reserved.</p>
    </footer>
</body>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>6. The Global Event Hub: Capturing vs. Bubbling</h2>
                <p>
                    Because the <code>&lt;body&gt;</code> is at the top of the DOM hierarchy,
                    it is the ultimate destination for events like <code>click</code>,
                    <code>mouseover</code>, and <code>keypress</code>.
                </p>

                <ProTip><p><strong>Performance Pattern: Event Delegation</strong>. Instead of attaching
                        listeners to 100 individual list items, attach <em>one</em> listener to the
                        body. Check <code>event.target</code> to determine which child was clicked.
                        This reduces memory overhead by 90% in large applications.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Body Delegation Pattern</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Global click handler at the body level
document.body.addEventListener('click', (event) => {
    // Check if the clicked element has a specific data attribute
    const action = event.target.closest('[data-action]');
    if (action) {
        handleGlobalAction(action.dataset.action);
    }
});`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. Accessibility: The ARIA Landmark Roadmap</h2>
                <p>
                    Screen readers use "Landmarks" to jump across the document body. While
                    semantic tags (<code>&lt;main&gt;</code>, <code>&lt;nav&gt;</code>)
                    automatically provide these roles, explicit ARIA roles are sometimes
                    required for complex application structures.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>role="banner"</h3>
                        <p>Applied to the global header. Usually contains the logo and site search.</p>
                    </div>
                    <div className="concept-card">
                        <h3>role="main"</h3>
                        <p>The core content area. There must only be one per document body.</p>
                    </div>
                    <div className="concept-card">
                        <h3>role="contentinfo"</h3>
                        <p>Applied to the footer. Contains copyright and legal links.</p>
                    </div>
                </div>

                <ProTip><p><strong>Pro Tip: "Skip to Main Content"</strong>. Always place a hidden-by-default
                        link at the very beginning of the <code>&lt;body&gt;</code>. This allows
                        keyboard users to bypass repetitive navigation menus and jump straight
                        to the content.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>8. Performance: The Critical Rendering Path</h2>
                <p>
                    The browser parses the <code>&lt;body&gt;</code> sequentially. Every script
                    tag or large image encountered during the initial parse can block the
                    <strong>Critical Rendering Path</strong>, leading to a "Flash of Unstyled
                    Content" (FOUC) or a blank screen.
                </p>

                <ProTip><p><strong>Optimize for LCP (Largest Contentful Paint):</strong> Place essential
                        hero images high up in the body and use <code>fetchpriority="high"</code>.
                        Conversely, push non-critical scripts to the very end of the body (just
                        before the closing <code>&lt;/body&gt;</code> tag) to ensure the DOM is
                        interactive as fast as possible.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Optimized Body Skeleton</span>
                    </div>
                    <CodeBlock language="html" code={`<body>
    <!-- 1. Instant Visibility (Critical CSS & HTML) --&gt;
    <header>...</header>
    <main>...</main>

    <!-- 2. Interactive Logic (Deferred Scripts) --&gt;
    <script src="heavy-bundle.js" defer></script>
</body>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>9. The Box Model & BFC: The Body's Physical Limits</h2>
                <p>
                    The <code>&lt;body&gt;</code> element is often confused with the
                    <strong>Viewport</strong>. However, the body is a block-level element
                    that can have its own dimensions, margins, and overflow settings.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Root BFC</h3>
                        <p>The body establishes the root Block Formatting Context. This means
                            it handles the "clearing" of floats and the containment of internal
                            layout logic.</p>
                    </div>
                    <div className="concept-card">
                        <h3>The Default Margin</h3>
                        <p>Browsers apply a default <code>8px</code> margin to the body. Modern
                            resets (like Normalize.css) set this to <code>0</code> to allow
                            edge-to-edge designs.</p>
                    </div>
                </div>

                <ProTip><p><strong>A Common CSS Gotcha:</strong> If you apply a background color
                        to the <code>&lt;body&gt;</code>, the browser "propagates" it to the
                        <code>&lt;html&gt;</code> and the entire canvas, even if the body has
                        zero height. This is a unique behavior of the body element.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Global State Management via the Body</h2>
                <p>
                    In large-scale applications, the body serves as the "Source of Truth" for
                    global UI states like Modal-Open, Sidebar-Collapsed, or High-Contrast-Mode.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>State Propagation via ClassList</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Preventing background scroll when a modal is open
function toggleModal(isOpen) {
    if (isOpen) {
        document.body.classList.add('modal-open');
    } else {
        document.body.classList.remove('modal-open');
    }
}

/* CSS */
body.modal-open {
    overflow: hidden;
    padding-right: 15px; /* Prevent layout shift from scrollbar disappearance */
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Body Content Patterns</h2>

                <div className="pattern-card">
                    <h3>1. Marketing/Landing Page</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<body>
    <header>
        <nav><!-- Navigation --&gt;</nav>
    </header>

    <main>
        <section id="hero">
            <h1>Amazing Product</h1>
            <p>Transform your workflow</p>
            <button>Get Started</button>
        </section>

        <section id="features">
            <h2>Features</h2>
            <!-- Feature cards --&gt;
        </section>

        <section id="testimonials">
            <h2>What Our Customers Say</h2>
            <!-- Testimonials --&gt;
        </section>

        <section id="cta">
            <h2>Ready to Get Started?</h2>
            <button>Sign Up Now</button>
        </section>
    </main>

    <footer><!-- Footer content --&gt;</footer>
</body>`} />
                    </div>
                </div>

                <div className="pattern-card">
                    <h3>2. Blog/Article Page</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<body>
    <header>
        <nav><!-- Navigation --&gt;</nav>
    </header>

    <main>
        <article>
            <header>
                <h1>Article Title</h1>
                <p>By <span class="author">John Doe</span> on <time datetime="2025-01-15">January 15, 2025</time></p>
            </header>

            <section>
                <h2>Introduction</h2>
                <p>Article content...</p>
            </section>

            <section>
                <h2>Main Content</h2>
                <p>More content...</p>
            </section>

            <footer>
                <p>Tags: <a href="/tag/html">HTML</a>, <a href="/tag/web">Web Development</a></p>
            </footer>
        </article>

        <aside>
            <h3>Related Articles</h3>
            <!-- Related links --&gt;
        </aside>
    </main>

    <footer><!-- Site footer --&gt;</footer>
</body>`} />
                    </div>
                </div>

                <div className="pattern-card">
                    <h3>3. E-commerce Product Page</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<body>
    <header>
        <nav><!-- Navigation --&gt;</nav>
    </header>

    <main>
        <article itemscope itemtype="https://schema.org/Product">
            <header>
                <h1 itemprop="name">Product Name</h1>
            </header>

            <section class="product-images">
                <img itemprop="image" src="product.jpg" alt="Product">
            </section>

            <section class="product-details">
                <p itemprop="description">Product description...</p>
                <p>Price: <span itemprop="price">$99.99</span></p>
                <button>Add to Cart</button>
            </section>

            <section class="reviews">
                <h2>Customer Reviews</h2>
                <!-- Reviews --&gt;
            </section>
        </article>

        <aside>
            <h3>Related Products</h3>
            <!-- Product recommendations --&gt;
        </aside>
    </main>

    <footer><!-- Site footer --&gt;</footer>
</body>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Body Content Organization Best Practices</h2>

                <div className="best-practices-list">
                    <div className="practice-item">
                        <h3>1. Use Semantic HTML</h3>
                        <p>
                            Prefer semantic elements (<code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>,
                            <code>&lt;main&gt;</code>) over generic <code>&lt;div&gt;</code> elements.
                            This improves accessibility and SEO.
                        </p>
                        <div className="comparison">
                            <div className="bad">
                                <strong> Avoid:</strong>
                                <CodeBlock language="html" code={`<div id="header">
    <div id="nav">...</div>
</div>
<div id="content">...</div>
<div id="footer">...</div>`} />
                            </div>
                            <div className="good">
                                <strong>Better:</strong>
                                <CodeBlock language="html" code={`<header>
    <nav>...</nav>
</header>
<main>...</main>
<footer>...</footer>`} />
                            </div>
                        </div>
                    </div>

                    <div className="practice-item">
                        <h3>2. One &lt;main&gt; Element Per Page</h3>
                        <p>
                            Use exactly one <code>&lt;main&gt;</code> element to identify the primary content.
                            This helps screen readers and search engines understand page structure.
                        </p>
                    </div>

                    <div className="practice-item">
                        <h3>3. Logical Heading Hierarchy</h3>
                        <p>
                            Use headings (<code>&lt;h1&gt;</code> through <code>&lt;h6&gt;</code>) in order.
                            Don't skip levels.
                        </p>
                        <div className="comparison">
                            <div className="bad">
                                <strong> Wrong:</strong>
                                <CodeBlock language="html" code={`<h1>Page Title</h1>
<h4>Subsection</h4>  <!-- Skips h2 and h3 --&gt;
<h2>Section</h2>     <!-- Out of order --&gt;`} />
                            </div>
                            <div className="good">
                                <strong>Correct:</strong>
                                <CodeBlock language="html" code={`<h1>Page Title</h1>
<h2>Section</h2>
<h3>Subsection</h3>
<h4>Sub-subsection</h4>`} />
                            </div>
                        </div>
                    </div>

                    <div className="practice-item">
                        <h3>4. Keep Body Clean</h3>
                        <p>
                            Move scripts to the end of the body (or use <code>defer</code>/<code>async</code> in the head).
                            This prevents blocking page rendering.
                        </p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<body>
    <!-- Content --&gt;
    <main>...</main>

    <!-- Scripts at the end --&gt;
    <script src="app.js"></script>
</body>`} />
                        </div>
                    </div>

                    <div className="practice-item">
                        <h3>5. Skip to Main Content Link</h3>
                        <p>
                            Add a "skip to main content" link for keyboard users (especially screen reader users).
                        </p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<body>
    <a href="#main-content" class="skip-link">Skip to main content</a>
    
    <header>
        <nav><!-- Long navigation menu --&gt;</nav>
    </header>

    <main id="main-content">
        <!-- Main content --&gt;
    </main>
</body>`} />
                        </div>
                        <p>Style the skip link to be visually hidden but focusable:</p>
                        <div className="code-example">
                            <CodeBlock language="css" code={`.skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: #000;
    color: #fff;
    padding: 8px;
    z-index: 100;
}

.skip-link:focus {
    top: 0;
}`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Considerations</h2>

                <div className="accessibility-tips">
                    <ul>
                        <li>
                            <strong>Landmarks:</strong> Semantic elements create ARIA landmarks automatically.
                            Screen readers use these to navigate the page.
                        </li>
                        <li>
                            <strong>Focus Management:</strong> Ensure interactive elements (links, buttons, forms)
                            are keyboard-accessible with proper tab order.
                        </li>
                        <li>
                            <strong>Text Alternatives:</strong> Provide alt text for images, transcripts for audio,
                            and captions for video.
                        </li>
                        <li>
                            <strong>Color Contrast:</strong> Maintain sufficient contrast between text and background
                            (WCAG recommends 4.5:1 for normal text).
                        </li>
                        <li>
                            <strong>Responsive Text:</strong> Use relative units (rem, em) for font sizes to respect
                            user's browser settings.
                        </li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Body Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Professional Body Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Professional Website | Home</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body class="home-page" data-theme="light">
    <!-- Skip link for accessibility --&gt;
    <a href="#main-content" class="skip-link">Skip to main content</a>

    <!-- Site header --&gt;
    <header role="banner">
        <div class="logo">
            <img src="logo.svg" alt="Company Logo">
        </div>
        <nav role="navigation" aria-label="Main navigation">
            <ul>
                <li><a href="/">Home</a></li>
                <li><a href="/about">About</a></li>
                <li><a href="/services">Services</a></li>
                <li><a href="/contact">Contact</a></li>
            </ul>
        </nav>
    </header>

    <!-- Main content --&gt;
    <main id="main-content" role="main">
        <!-- Hero section --&gt;
        <section class="hero" aria-labelledby="hero-heading">
            <h1 id="hero-heading">Welcome to Our Service</h1>
            <p>Transform your business with our innovative solutions.</p>
            <button type="button">Get Started</button>
        </section>

        <!-- Features section --&gt;
        <section class="features" aria-labelledby="features-heading">
            <h2 id="features-heading">Our Features</h2>
            <div class="feature-grid">
                <article class="feature-card">
                    <h3>Feature One</h3>
                    <p>Description of feature one...</p>
                </article>
                <article class="feature-card">
                    <h3>Feature Two</h3>
                    <p>Description of feature two...</p>
                </article>
                <article class="feature-card">
                    <h3>Feature Three</h3>
                    <p>Description of feature three...</p>
                </article>
            </div>
        </section>

        <!-- Sidebar --&gt;
        <aside role="complementary" aria-labelledby="sidebar-heading">
            <h2 id="sidebar-heading">Related Information</h2>
            <ul>
                <li><a href="/blog">Our Blog</a></li>
                <li><a href="/resources">Resources</a></li>
            </ul>
        </aside>
    </main>

    <!-- Site footer --&gt;
    <footer role="contentinfo">
        <div class="footer-content">
            <section class="footer-section">
                <h3>About Us</h3>
                <p>Company description...</p>
            </section>
            <section class="footer-section">
                <h3>Quick Links</h3>
                <ul>
                    <li><a href="/privacy">Privacy Policy</a></li>
                    <li><a href="/terms">Terms of Service</a></li>
                </ul>
            </section>
        </div>
        <p class="copyright">&copy; 2025 Company Name. All rights reserved.</p>
    </footer>

    <!-- Scripts at the end --&gt;
    <script src="app.js"></script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>11. Security: The Isolation of the Body</h2>
                <p>
                    The <code>&lt;body&gt;</code> is the target of various security policies.
                    Understanding how browsers isolate content within the body is
                    essential for protecting user data.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>CORS Isolation</h3>
                        <p>When loading resources (images, scripts) into the body, the browser
                            enforces Same-Origin Policy. Use <code>crossorigin</code> attributes
                            to manage authenticated requests.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Permissions Policy</h3>
                        <p>Modern browsers allow headers to restrict which APIs (Camera,
                            Microphone) can be accessed by the body or its child iframes.</p>
                    </div>
                </div>

                <ProTip><p><strong>Security Tip:</strong> Avoid <code>javascript:</code> URLs in
                        links within the body. Use actual event listeners. This mitigates
                        <strong>Cross-Site Scripting (XSS)</strong> risks by centralizing
                        your executable logic in dedicated script files.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>12. The Scrolling Masterclass: Viewport vs. Body</h2>
                <p>
                    Scrolling is one of the most critical aspects of user experience.
                    Modern CSS allows you to control how the body behaves when content
                    overflows.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Advanced Scrolling CSS</span>
                    </div>
                    <CodeBlock language="css" code={`body {
    /* Prevent the 'bounce' effect on mobile headers/footers */
    overscroll-behavior-y: none;
    
    /* Ensure smooth transitions for anchor links */
    scroll-behavior: smooth;
    
    /* Reserve space for the scrollbar to prevent layout shift */
    scrollbar-gutter: stable;
}`} />
                </div>

                <ProTip><p><strong>The "Scroll-Lock" Pattern:</strong> When building modals,
                        developers often set <code>overflow: hidden</code> on the body. To
                        prevent the background from jumping, calculate the scrollbar width
                        and add it as <code>padding-right</code> to the body while the
                        modal is active.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>13. Case Study: Single Page Application (SPA) Strategy</h2>
                <p>
                    In frameworks like React or Next.js, the <code>&lt;body&gt;</code>
                    rarely changes. Instead, a single <code>&lt;div id="root"&gt;</code>
                    is injected. However, the body still handles:
                </p>
                <ul>
                    <li><strong>Global Theme Classes:</strong> Controlled by a <code>DarkThemeContext</code>.</li>
                    <li><strong>Global Keyboard Listeners:</strong> For shortcuts like <code>Ctrl+K</code> search.</li>
                    <li><strong>Viewport Resizing:</strong> Managed by a global <code>ResizeObserver</code> on the body.</li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Body-Level Theme Controller</span>
                    </div>
                    <CodeBlock language="javascript" code={`// React logic for body management
useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
    document.body.dir = isRTL ? 'rtl' : 'ltr';
}, [isDarkMode, isRTL]);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>14. Debugging & Developer Excellence: The Body Dashboard</h2>
                <p>
                    Professional developers don't just write <code>&lt;body&gt;</code> tags;
                    they audit them. The body is the first point of failure for many
                    performance and security issues.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>CLS Auditing</h3>
                        <p>Cumulative Layout Shift (CLS) often starts at the body level. Use
                            Chrome DevTools 'Performance' tab to see which body children are
                            causing shifts during initial load.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Computed Styles</h3>
                        <p>Always check the <code>Computed</code> tab in DevTools for the body.
                            Browsers often inject 'User Agent Styles' (like that 8px margin)
                            that can ruin a pixel-perfect design.</p>
                    </div>
                </div>

                <ProTip><p><strong>Auditing Tip:</strong> Open your DevTools console and type
                        <code>document.body.querySelectorAll('*').length</code>. If this number
                        exceeds 1,500, your body content is too "deep" (DOM depth), which
                        can significantly slow down rendering on mobile devices.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>15. Historical Evolution: From Applets to SPAs</h2>
                <p>
                    The <code>&lt;body&gt;</code> element has transformed significantly since
                    HTML 2.0. In the 90s, the body was often shared with the now-obsolete
                    <code>&lt;frameset&gt;</code> tag, which split the screen into multiple
                    independent documents.
                </p>
                <p>
                    Today, the body is a clean, semantic container. We no longer use
                    body attributes like <code>bgcolor</code> or <code>text</code>,
                    favoring CSS for all visual representation. This separation of
                    concernsHTML for structure, CSS for presentation, and JS for
                    behavioris the "Holy Trinity" of web development.
                </p>
                <ProTip><p><strong>Did You Know?</strong> In the early days, you could put
                        Java Applets directly in the body to have complex logic before
                        JavaScript was powerful enough to handle it. We've come a long way!</p></ProTip>
            </div>

            <div className="content-section">
                <h2>16. Final Checklist: The Super-Premium Body</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Technical Health</h3>
                        <ul>
                            <li>One <code>&lt;main&gt;</code> element present.</li>
                            <li>Skip link provided for accessibility.</li>
                            <li>Scripts are deferred or at the end.</li>
                            <li>No default margin (CSS Reset applied).</li>
                            <li>LCP images highpriority.</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>17. Conclusion: The Body as a Dynamic Lifecycle</h2>
                <p>
                    Ultimately, the <code>&lt;body&gt;</code> is not just a container; it is a
                    living, breathing entity in the browser's memory. Every DOM manipulation,
                    every style recalculation, and every event bubble flows through this
                    single element. By mastering the body, you master the very foundation
                    of web interaction.
                </p>
                <p>
                    Whether you are building a static landing page or
                    a complex Single Page Application (SPA), the principles of semantic structure,
                    accessibility landmarks, and performance optimization remain the same.
                    The body is your architectural canvasuse it with precision, intent,
                    and a deep respect for the user's experience. Remember that a clean body
                    results in a clean application state, faster rendering, and a more
                    inclusive web for everyone.
                </p>
            </div>

        </div>
    );
};

export default HTMLBody;



